import {
    Grid,
    InputAdornment,
    styled,
    Box,
    IconButton,
    AutocompleteChangeReason,
    AutocompleteRenderInputParams,
} from "@mui/material";
import { FC, useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { URLSearchParamsInit, useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import IotVisionTextFiled from "components/common/IotVisionTextFiled";
import IotVisionToolTips from "components/common/IotVisionToolTips";
import IotVisionButton from "components/common/IotVisionButton";
import IotVisionAutoComplete from "components/common/IotVisonAutoComplete";
import { DeviceType } from "enums/device.enums";
import { humanize } from "utils/common";
import { DeviceHeartbeatStatus } from "enums/heartbeat.enum";

interface DeviceMonitorFilters {
    name: string;
    deviceType: { id: string; label: string };
    status: { id: string; label: string };
}

interface AutoLabelId {
    label: string;
    id: string;
}

interface DeviceMonitorFilterPanelProps {
    onRefresh: () => void;
    isRefreshing: boolean;
}

const defaultValues: DeviceMonitorFilters = {
    name: "",
    deviceType: { id: "", label: "" },
    status: { id: "", label: "" },
};

const StyledIconButton = styled(IconButton)(({ theme, disabled }) => ({
    borderRadius: "20px",
    height: "37px",
    border: "1px solid",
    borderColor: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
    color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
    backgroundColor: "white",
}));

const DeviceMonitorFilterPanel: FC<DeviceMonitorFilterPanelProps> = ({ onRefresh, isRefreshing }) => {
    const { handleSubmit, control, setValue, reset } = useForm<DeviceMonitorFilters>({
        defaultValues,
    });
    const [searchParams, setSearchParams] = useSearchParams();

    const { name, deviceType, status } = Object.fromEntries(searchParams);

    const onSearch: SubmitHandler<DeviceMonitorFilters> = (data: DeviceMonitorFilters) => {
        const searchParamData: URLSearchParamsInit | undefined = {
            ...(data.name.trim() ? { name: data.name.trim() } : {}),
            ...(data.deviceType.id ? { deviceType: data.deviceType.id } : {}),
            ...(data.status.id ? { status: data.status.id } : {}),
        };
        setSearchParams(searchParamData);
    };

    const onClear = () => {
        reset(defaultValues);
        setSearchParams({});
    };

    useEffect(() => {
        reset({
            name: name?.trim(),
            deviceType: Object.values(DeviceType)
                .map((type) => type.toLowerCase())
                .includes(deviceType)
                ? {
                      id: deviceType,
                      label:
                          deviceType.toUpperCase() === DeviceType.GPU ? deviceType.toUpperCase() : humanize(deviceType),
                  }
                : { id: "", label: "" },
            status: Object.values(DeviceHeartbeatStatus).includes(status as DeviceHeartbeatStatus)
                ? { id: status, label: status }
                : { id: "", label: "" },
        });
    }, [name, deviceType, status]);

    return (
        <form onSubmit={handleSubmit(onSearch)}>
            <Grid container spacing={1} alignItems="center" mt={1} mb={1}>
                <Grid item xs={12} md={3}>
                    <Controller
                        name="deviceType"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                options={Object.values(DeviceType).map((type) => ({
                                    id: type.toLowerCase(),
                                    label: type.toUpperCase() === DeviceType.GPU ? type.toUpperCase() : humanize(type),
                                }))}
                                value={value}
                                onChange={(
                                    _event: React.SyntheticEvent,
                                    newValue: AutoLabelId,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        const data = { label: "", id: "" };
                                        setValue("deviceType", data, { shouldDirty: true });
                                    } else {
                                        const data = { label: newValue?.label, id: newValue?.id };
                                        setValue("deviceType", data, { shouldDirty: true });
                                    }
                                }}
                                renderInput={(params: Omit<AutocompleteRenderInputParams, "fullWidth">) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder="Select device type" />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionTextFiled
                                fullWidth
                                type="text"
                                placeholder="Search by device name..."
                                autoComplete="off"
                                value={value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setValue("name", event.target.value, { shouldDirty: true })
                                }
                                InputProps={{
                                    endAdornment: value ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{
                                                    fontSize: "14px",
                                                    borderRadius: "50%",
                                                }}
                                                size="small"
                                                onClick={() => setValue("name", "", { shouldDirty: true })}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                options={Object.values(DeviceHeartbeatStatus).map((status) => ({
                                    id: status,
                                    label: status,
                                }))}
                                value={value}
                                onChange={(
                                    _event: React.SyntheticEvent,
                                    newValue: AutoLabelId,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        const data = { label: "", id: "" };
                                        setValue("status", data, { shouldDirty: true });
                                    } else {
                                        const data = { label: newValue?.label, id: newValue?.id };
                                        setValue("status", data, { shouldDirty: true });
                                    }
                                }}
                                renderInput={(params: Omit<AutocompleteRenderInputParams, "fullWidth">) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder="Select status" />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid container item xs={12} md={3}>
                    <Box mr={1}>
                        <IotVisionToolTips title="Refresh" PopperProps={{ disablePortal: true }} arrow>
                            <span>
                                <StyledIconButton disabled={isRefreshing} onClick={onRefresh}>
                                    <RefreshIcon />
                                </StyledIconButton>
                            </span>
                        </IotVisionToolTips>
                    </Box>
                    <Grid container columns={2} flex={1} spacing={1}>
                        <Grid item xs={1} sm={1} md={1} lg={1} xl={1}>
                            <IotVisionButton
                                fullWidth
                                type="submit"
                                sx={{
                                    height: "37px",
                                    color: "white !important",
                                }}
                            >
                                Search
                            </IotVisionButton>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1} xl={1}>
                            <IotVisionButton
                                fullWidth
                                onClick={onClear}
                                sx={{
                                    height: "37px",
                                    backgroundColor: "white",
                                    color: "#6b439d !important",
                                    "&:hover": {
                                        background: "white",
                                    },
                                }}
                            >
                                Clear
                            </IotVisionButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default DeviceMonitorFilterPanel;
